import { OauthService } from '../../api/services/oauth.service';
import { InputEventUtils } from '../../utils/input_event_utils';
import { oauthAccountView } from './oauth-account.view';
import { OauthSessionPopup } from './oauth-session-popup';
import { OauthCompletePopup } from './oauth-complete-popup';
/**
 * OAuth sign-in control bound to the `#oauth-account` button.
 *
 * Sign-in uses a multi-domain flow: clicking the button (when logged out)
 * redirects the top-level page to the central auth selector on a different
 * domain. After the provider callback the browser is redirected back here, where
 * the resulting token is pulled over via a hidden postMessage relay iframe and
 * stored under the shared `accesstoken` key — so the rest of the app logs in
 * exactly as it would for an email/password sign-in. When already logged in,
 * clicking the button signs out. See {@link OauthService} for the wire contract.
 */
export class OauthAccount {
    constructor() {
        this.user = null;
        this.sessionPopup = null;
        this.completePopup = null;
        this.oauthService = new OauthService();
        this.buttonEl = document.getElementById('oauth-account');
        if (!this.buttonEl) {
            return;
        }
        this.onButtonClick = this.onButtonClick.bind(this);
        this.buttonEl.innerHTML = oauthAccountView();
        InputEventUtils.addSelectEvents(this.buttonEl, this.onButtonClick);
        this.buttonEl.classList.remove('is-hidden');
        this.setLoggedInState(null);
        if (this.oauthService.isReturningFromAuth()) {
            this.promptCompleteSignIn();
        }
        else {
            void this.loadSession();
        }
    }
    /**
     * Ask the user to finish the OAuth handshake. The token is retrieved through a
     * popup, which browsers only allow from a user gesture — so we can't complete
     * silently on page load and instead surface a "Complete sign-in" button. The
     * return marker is stripped from the URL right away so a refresh won't re-prompt.
     */
    promptCompleteSignIn() {
        this.oauthService.consumeReturnMarker();
        this.completePopup = new OauthCompletePopup({
            onComplete: () => this.oauthService.completeSignIn(),
            onSuccess: () => {
                this.completePopup = null;
                void this.loadSession();
            },
            onCancel: () => {
                this.completePopup = null;
                this.setLoggedInState(null);
            }
        });
    }
    onButtonClick() {
        if (this.user) {
            this.openSessionPopup(this.user);
            return;
        }
        this.oauthService.beginSignIn();
    }
    openSessionPopup(user) {
        var _a;
        (_a = this.sessionPopup) === null || _a === void 0 ? void 0 : _a.close();
        this.sessionPopup = new OauthSessionPopup(user, () => this.signOut());
    }
    loadSession() {
        return this.oauthService
            .getSession()
            .then(({ user }) => this.setLoggedInState(user))
            .catch(() => this.setLoggedInState(null));
    }
    signOut() {
        this.oauthService.signOut();
        this.setLoggedInState(null);
        this.sessionPopup = null;
    }
    setLoggedInState(user) {
        this.user = user;
        if (!this.buttonEl) {
            return;
        }
        const isLoggedIn = !!user;
        this.buttonEl.classList.toggle('is-logged-in', isLoggedIn);
        this.buttonEl.setAttribute('aria-label', isLoggedIn ? `Sign out ${user.display_name || user.email || ''}`.trim() : 'Sign in');
    }
    dispose() {
        var _a, _b, _c;
        (_a = this.sessionPopup) === null || _a === void 0 ? void 0 : _a.close();
        this.sessionPopup = null;
        (_b = this.completePopup) === null || _b === void 0 ? void 0 : _b.close();
        this.completePopup = null;
        (_c = this.buttonEl) === null || _c === void 0 ? void 0 : _c.removeEventListener('click', this.onButtonClick);
    }
}
